import { useCallback, useEffect, useRef } from 'react';

/**
 * React hook for Cloudflare Turnstile integration.
 *
 * Renders an invisible/managed Turnstile widget into a container element
 * and exposes `getToken()` to retrieve the current verification token.
 *
 * Call `resetWidget()` after each form submission — Turnstile tokens are single-use.
 *
 * @param containerId  The DOM element ID where the widget should render (default: 'turnstile-widget')
 * @param action       An optional action string sent with the verification (e.g. 'login', 'forgot_password')
 */
export const useTurnstile = (containerId = 'turnstile-widget', action?: string) => {
    const widgetIdRef = useRef<string | null>(null);
    const tokenRef = useRef<string | undefined>(undefined);
    const renderedRef = useRef(false);

    const isEnabled = typeof window !== 'undefined' && window.TURNSTILE_ENABLED;
    const siteKey = typeof window !== 'undefined' ? window.TURNSTILE_SITE_KEY : '';

    // Render the widget once the Turnstile script is loaded
    useEffect(() => {
        if (!isEnabled || !siteKey || renderedRef.current) return;

        const tryRender = () => {
            const container = document.getElementById(containerId);
            if (!container || !window.turnstile) return false;

            // Clear any previous widget in this container
            container.innerHTML = '';
            tokenRef.current = undefined;

            widgetIdRef.current = window.turnstile.render(container, {
                sitekey: siteKey,
                callback: (token: string) => {
                    tokenRef.current = token;
                },
                'expired-callback': () => {
                    tokenRef.current = undefined;
                    if (widgetIdRef.current && window.turnstile) {
                        window.turnstile.reset(widgetIdRef.current);
                    }
                },
                'error-callback': () => {
                    tokenRef.current = undefined;
                },
                action,
                appearance: 'interaction-only',
                size: 'normal',
            });

            renderedRef.current = true;
            return true;
        };

        // The Turnstile script loads async — poll briefly if not ready yet
        if (tryRender()) return;

        const interval = setInterval(() => {
            if (tryRender()) {
                clearInterval(interval);
            }
        }, 200);

        // Stop polling after 10 seconds
        const timeout = setTimeout(() => clearInterval(interval), 10_000);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, [isEnabled, siteKey, containerId, action]);

    // Cleanup on unmount — clear refs only, avoid turnstile.remove() which issues an extra challenge
    useEffect(() => {
        return () => {
            widgetIdRef.current = null;
            tokenRef.current = undefined;
            renderedRef.current = false;
        };
    }, []);

    const getToken = useCallback((): string | undefined => {
        if (!isEnabled) return undefined;

        // Try the ref first, fall back to turnstile.getResponse
        if (tokenRef.current) return tokenRef.current;

        if (widgetIdRef.current && window.turnstile) {
            return window.turnstile.getResponse(widgetIdRef.current) ?? undefined;
        }

        return undefined;
    }, [isEnabled]);

    const resetWidget = useCallback(() => {
        tokenRef.current = undefined;
        if (widgetIdRef.current && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
        }
    }, []);

    return { getToken, resetWidget, isEnabled };
};
